import React, {useState, useEffect} from 'react';
import {Dropdown, Grid} from 'semantic-ui-react';
import {FemaGoTable} from 'app-ui-componentlibrary';
import {connectDynamic} from 'go/components/dynamic/dynamicComponent';
import _ from 'lodash';
import {
  formatDateMMDDYYYY,
  safeAdd,
  showDollarAmountWithDollar,
  onlyAllowNumbers,
} from 'common/utils';
import {ASSOCIATED_ENTITY_STATUSES} from 'app-ui-componentlibrary/dist/common/constants';
import REST from 'go/requests/rest';
import {isRequired, greaterThan, regexMatch} from 'go/components/rules/rules';
import {Link} from 'react-router-dom';

export const SubApplicationsTable = (props) => {
  const {
    subapplications,
    selectedColumn,
    sortDirection,
    setSelectedColumnProp,
    setSortDirectionProp,
    summary,
    doEntityAction,
    saveEntitySessionData,
    modifySubApp,
    updatePieChart,
    applicationPeriodNotOpen,
  } = props;
  const readOnly = _.get(props, 'readOnly', true);
  const [subapplicationsState, setSubApplications] = useState(subapplications);

  const columnsToHide = _.get(props, 'hideColumns', []);
  const fundingCategories = _.map(_.get(props, 'fundingCategories', []), (fC) => {
    return {key: fC.name, value: fC.name, text: fC.name, rank: !!fC.rank};
  });

  const handleColumnClick = (columnName) => {
    let direction = sortDirection == 'ASC' ? 'DESC' : 'ASC';
    setSelectedColumnProp(columnName);
    setSortDirectionProp(direction);
  };
  const getClassName = (columnName) =>
    `sorted${
      selectedColumn == columnName
        ? sortDirection == 'ASC'
          ? ' ascending'
          : ' descending'
        : ''
    }`;
  const updateSubappValue = (i, name, value, persist) => {
    if (name === 'rank' && value) {
      const allowOnlyPositive = true;
      value = onlyAllowNumbers(value, allowOnlyPositive);
    }
    const updatedSubApp = {
      ...subapplicationsState[i],
      payload: {
        ...subapplicationsState[i].payload,
        [name]: value,
      },
    };

    if (!value) {
      _.unset(updatedSubApp, `payload.${name}`);
    }

    if (
      name === 'fundingCategory' &&
      hasRank(_.get(subapplicationsState[i].payload, 'fundingCategory')) &&
      !hasRank(value)
    ) {
      delete updatedSubApp.payload.rank;
    }
    const updatedSubApplicationState = [
      ...subapplicationsState.slice(0, i),
      updatedSubApp,
      ...subapplicationsState.slice(i + 1),
    ];
    setSubApplications(updatedSubApplicationState);

    const valid = validateTable(updatedSubApplicationState, i);

    const propsToSave = {
      'payload.fundingCategory': _.get(updatedSubApp, 'payload.fundingCategory', ''),
      'payload.rank': _.get(updatedSubApp, 'payload.rank', ''),
    };

    const oldFundingCategory = subapplicationsState[i].payload.fundingCategory;
    const newFundingCategory = _.get(
      propsToSave,
      'payload.fundingCategory',
      oldFundingCategory
    );
    if (oldFundingCategory !== newFundingCategory) {
      updatePieChart(
        oldFundingCategory,
        newFundingCategory,
        subapplicationsState[i].payload.budget.share.fedShare
      );
    }

    persist && valid && modifySubApp(updatedSubApp.entityId, propsToSave);
  };

  const hasRank = (fcName) =>
    _.find(fundingCategories, (fc) => fc.key === fcName && fc.rank);

  const validateRank = (rank) => {
    return (
      isRequired()(rank) &&
      greaterThan(0)(rank) &&
      onlyAllowNumbers(rank) &&
      regexMatch(/^\d+$/)(rank)
    );
  };

  const validateTable = (updatedSubApplicationState, i) => {
    let isValid = true;
    _.forEach(updatedSubApplicationState, (subApp, idx) => {
      const notValid =
        hasRank(_.get(subApp, 'payload.fundingCategory')) &&
        !validateRank(`${_.get(subApp, 'payload.rank')}`);
      if (idx === i) isValid = !notValid;
      saveEntitySessionData('context.subApps', {
        [subApp.entityId]: {
          isValid: notValid,
          title: subApp.payload.applicationTitle,
        },
      });
    });
    return isValid;
  };

  useEffect(() => {
    validateTable(subapplications);
  }, []);

  const renderActiveTabContents = () => {
    const hasRankColumn =
      !_.includes(columnsToHide, 'rank') &&
      !_.isEmpty(fundingCategories) &&
      _.find(fundingCategories, (fc) => fc.rank);

    return (
      <Grid
        key={'subApplicationsGrid'}
        doubling
        stackable
        reversed="mobile"
        padded="vertically"
      >
        <Grid.Column style={{overflow:'auto'}}>
          <FemaGoTable
            summary={summary || 'SubApplications table'}
            type="static"
            columns="16"
            className="sortable"
          >
            <FemaGoTable.Header>
              <FemaGoTable.Row>
                {!_.includes(columnsToHide, 'fundingCategory') ? (
                  <FemaGoTable.HeaderCell
                    title="Funding category"
                    onClick={() => handleColumnClick('fundingCategory')}
                    className={getClassName('fundingCategory')}
                  >
                    Funding<br></br>category
                  </FemaGoTable.HeaderCell>
                ) : null}
                {hasRankColumn ? (
                  <FemaGoTable.HeaderCell
                    title="Rank"
                    onClick={() => handleColumnClick('rank')}
                    className={getClassName('rank')}
                  >
                    Rank
                  </FemaGoTable.HeaderCell>
                ) : null}
                {!_.includes(columnsToHide, 'orgName') ? (
                  <FemaGoTable.HeaderCell
                    title="Recipient"
                    onClick={() => handleColumnClick('orgName')}
                    className={getClassName('orgName')}
                  >
                    Recipient
                  </FemaGoTable.HeaderCell>
                ) : null}
                {!_.includes(columnsToHide, 'subOrgName') ? (
                  <FemaGoTable.HeaderCell
                    title="Subrecipient"
                    onClick={() => handleColumnClick('subOrgName')}
                    className={getClassName('subOrgName')}
                  >
                    Subrecipient
                  </FemaGoTable.HeaderCell>
                ) : null}
                {!_.includes(columnsToHide, 'subGrantID') ? (
                  <FemaGoTable.HeaderCell
                    title="Subgrant ID"
                    onClick={() => handleColumnClick('subGrantID')}
                    className={getClassName('subGrantID')}
                  >
                    Subgrant ID
                  </FemaGoTable.HeaderCell>
                ) : null}
                {!_.includes(columnsToHide, 'applicationTitle') ? (
                  <FemaGoTable.HeaderCell
                    title="Title of subapplication"
                    onClick={() => handleColumnClick('applicationTitle')}
                    className={getClassName('applicationTitle')}
                  >
                    Title
                  </FemaGoTable.HeaderCell>
                ) : null}
                {!_.includes(columnsToHide, 'applicationTypeDescription') ? (
                  <FemaGoTable.HeaderCell
                    title="Type of subapplication"
                    onClick={() => handleColumnClick('applicationTypeDescription')}
                    className={getClassName('applicationTypeDescription')}
                  >
                    Type
                  </FemaGoTable.HeaderCell>
                ) : null}
                {!_.includes(columnsToHide, 'status') ? (
                  <FemaGoTable.HeaderCell
                    title="Status of subapplication"
                    onClick={() => handleColumnClick('status')}
                    className={getClassName('status')}
                  >
                    Status
                  </FemaGoTable.HeaderCell>
                ) : null}
                {!_.includes(columnsToHide, 'statusTimeStamp') ? (
                  <FemaGoTable.HeaderCell
                    title="Date of status"
                    onClick={() => handleColumnClick('statusTimeStamp')}
                    className={getClassName('statusTimeStamp')}
                  >
                    Status date
                  </FemaGoTable.HeaderCell>
                ) : null}
                {!_.includes(columnsToHide, 'fedShare') ? (
                  <FemaGoTable.HeaderCell
                    title="Requested federal share"
                    onClick={() => handleColumnClick('fedShare')}
                    className={getClassName('fedShare')}
                  >
                    Requested<br></br>federal share
                  </FemaGoTable.HeaderCell>
                ) : null}
                {!_.includes(columnsToHide, 'obligationAmount') ? (
                  <FemaGoTable.HeaderCell
                    title="Obligation amount"
                    style={{cursor: 'default'}}
                    className={getClassName('obligationAmount')}
                  >
                    Obligation<br></br>amount
                  </FemaGoTable.HeaderCell>
                ) : null}
                {!_.includes(columnsToHide, 'actions') ? (
                  <FemaGoTable.HeaderCell
                    title="Actions for subapplication"
                    style={{cursor: 'default'}}
                  >
                    Actions
                  </FemaGoTable.HeaderCell>
                ) : null}
              </FemaGoTable.Row>
            </FemaGoTable.Header>
            <FemaGoTable.Body>
              {_.map(subapplicationsState, (subapp, index) => {
                const actions = _.get(subapp, 'actions', []);
                let showError = false;
                let errorMessages = [];
                let displayRankError =
                  hasRank(_.get(subapp.payload, 'fundingCategory')) &&
                  !validateRank(`${_.get(subapp.payload, 'rank')}`);
                if (displayRankError) {
                  showError = true;
                  errorMessages.push('Ranks must be positive whole numbers.');
                }
                return (
                  <FemaGoTable.Row
                    key={`${subapp.entityId}`}
                    errorMessages={errorMessages}
                    displayError={showError}
                  >
                    {!_.includes(columnsToHide, 'fundingCategory') ? (
                      !readOnly &&
                      !applicationPeriodNotOpen() &&
                      !_.isEmpty(fundingCategories) ? (
                        <FemaGoTable.SelectCell
                          width={3}
                          options={fundingCategories}
                          name="fundingCategory"
                          className="item"
                          size="small"
                          value={_.get(subapp.payload, 'fundingCategory')}
                          handleChange={(e) =>
                            updateSubappValue(
                              index,
                              'fundingCategory',
                              e.target.value,
                              false
                            )
                          }
                          handleBlur={(e) =>
                            updateSubappValue(
                              index,
                              'fundingCategory',
                              e.target.value,
                              true
                            )
                          }
                        />
                      ) : (
                        <FemaGoTable.TextCell>
                          {_.get(subapp.payload, 'fundingCategory')}
                        </FemaGoTable.TextCell>
                      )
                    ) : null}
                    {hasRankColumn ? (
                      readOnly ||
                      applicationPeriodNotOpen() ||
                      !hasRank(_.get(subapp.payload, 'fundingCategory')) ? (
                        <FemaGoTable.TextCell width={1}>
                          {_.get(subapp.payload, 'rank')}
                        </FemaGoTable.TextCell>
                      ) : (
                        <FemaGoTable.InputCell
                          width={1}
                          id={`rank-${index}`}
                          name="rank"
                          size="small"
                          value={_.get(subapp.payload, 'rank')}
                          handleChange={(e) =>
                            updateSubappValue(index, 'rank', e.target.value, false)
                          }
                          handleBlur={(e) =>
                            updateSubappValue(index, 'rank', e.target.value, true)
                          }
                          displayError={displayRankError}
                        />
                      )
                    ) : null}
                    {!_.includes(columnsToHide, 'orgName') ? (
                      <FemaGoTable.TextCell>
                        {_.get(subapp.payload, 'orgName')}
                      </FemaGoTable.TextCell>
                    ) : null}
                    {!_.includes(columnsToHide, 'subOrgName') ? (
                      <FemaGoTable.TextCell>
                        {_.get(subapp.payload, 'subOrgName')}
                      </FemaGoTable.TextCell>
                    ) : null}
                    {!_.includes(columnsToHide, 'subGrantID') ? (
                      <FemaGoTable.TextCell>
                        <a href={'/subGrant/' + subapp.subGrantId} target="_blank">
                          {subapp.subGrantId}
                        </a>
                      </FemaGoTable.TextCell>
                    ) : null}
                    {!_.includes(columnsToHide, 'applicationTitle') ? (
                      <FemaGoTable.TextCell>
                        {_.get(subapp.payload, 'applicationTitle')}
                      </FemaGoTable.TextCell>
                    ) : null}
                    {!_.includes(columnsToHide, 'applicationTypeDescription') ? (
                      <FemaGoTable.TextCell>
                        {_.get(subapp.payload, 'applicationTypeDescription')}
                      </FemaGoTable.TextCell>
                    ) : null}
                    {!_.includes(columnsToHide, 'status') ? (
                      <FemaGoTable.TextCell>{`${
                        ASSOCIATED_ENTITY_STATUSES[subapp.status]
                      }`}</FemaGoTable.TextCell>
                    ) : null}
                    {!_.includes(columnsToHide, 'statusTimeStamp') ? (
                      <FemaGoTable.TextCell>
                        {formatDateMMDDYYYY(_.get(subapp, 'statusTimestamp'))}
                      </FemaGoTable.TextCell>
                    ) : null}
                    {!_.includes(columnsToHide, 'fedShare') ? (
                      <FemaGoTable.TextCell>
                        {showDollarAmountWithDollar(
                          safeAdd(..._.get(subapp.payload, 'budget.share.fedShare', []))
                        )}
                      </FemaGoTable.TextCell>
                    ) : null}
                    {!_.includes(columnsToHide, 'obligationAmount') ? (
                      <FemaGoTable.TextCell>
                        {showDollarAmountWithDollar(_.get(subapp, 'awardedObligation'))}
                      </FemaGoTable.TextCell>
                    ) : null}
                    {!_.includes(columnsToHide, 'actions') ? (
                      <FemaGoTable.TextCell>
                        {!_.isEmpty(actions) ? (
                          <Dropdown
                            selectOnNavigation={false}
                            selectOnBlur={false}
                            text={'Actions'}
                            name={`${subapp.entityId}-action-button`}
                            id={`${subapp.entityId}-action-button`}
                            onChange={(e, {name, value}) => doEntityAction(value, subapp)}
                            options={actions}
                            direction="left"
                            value={''}
                          />
                        ) : null}
                      </FemaGoTable.TextCell>
                    ) : null}
                  </FemaGoTable.Row>
                );
              })}
            </FemaGoTable.Body>
          </FemaGoTable>
        </Grid.Column>
      </Grid>
    );
  };

  return <>{renderActiveTabContents()}</>;
};

export default connectDynamic(SubApplicationsTable);
